import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Building2, Briefcase } from 'lucide-react';
import { ErrorBoundary } from 'react-error-boundary';
import { useLaunchDeals } from '../../hooks/useLaunchDeals';
import { useAppPermissions } from '../../hooks/useAppPermissions';
import LaunchDealCreateModal from './LaunchDealCreateModal';

const STAGE_COLORS = {
  novo:               'bg-slate-100 text-slate-700',
  em_qualificacao:    'bg-sky-100 text-sky-700',
  proposta_enviada:   'bg-violet-100 text-violet-700',
  em_negociacao:      'bg-amber-100 text-amber-700',
  contrato_assinado:  'bg-emerald-100 text-emerald-700',
  encerrado_ganho:    'bg-green-100 text-green-700',
  encerrado_perdido:  'bg-red-100 text-red-700',
};

function fmtBRL(v) {
  if (v == null) return '—';
  const n = Number(v);
  if (!isFinite(n)) return '—';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function TabFallback() {
  return (
    <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
      Falha ao carregar deals desta empresa. Recarregue a página ou contate o admin.
    </div>
  );
}

function EmpresaLaunchesTabBody({ empresaId }) {
  const { permissions } = useAppPermissions();
  const { deals, loading, error, refetch } = useLaunchDeals({ empresa_id: empresaId });
  const [createOpen, setCreateOpen] = useState(false);
  const canWrite = permissions.includes('launches.write');

  return (
    <div className="space-y-3">
      {/* OruloDeveloperPanel placeholder */}
      <div className="p-3 bg-violet-50/40 border border-violet-200 rounded">
        <div className="flex items-center gap-1.5 mb-1">
          <Building2 size={13} className="text-violet-600" />
          <div className="text-[11px] font-semibold text-violet-800">Inteligência Órulo</div>
        </div>
        <div className="text-[11px] text-violet-700/80 italic">
          OruloDeveloperPanel em construção (SET 4 — ADR-017 mirror somente-leitura).
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 flex items-center gap-1">
          <Briefcase size={10} /> Deals — Empreendimentos ({loading ? '...' : deals.length})
        </div>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          disabled={!canWrite}
          title={canWrite ? 'Criar novo deal vinculado a esta empresa' : 'Sem permissão de escrita.'}
          className="px-2 py-1 bg-violet-600 hover:bg-violet-700 text-white rounded text-[11px] flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus size={11} /> Novo
        </button>
      </div>

      {loading && (
        <div className="space-y-1.5">
          <div className="animate-pulse bg-slate-100 h-12 rounded" />
          <div className="animate-pulse bg-slate-100 h-12 rounded" />
        </div>
      )}
      {!loading && error && <TabFallback />}
      {!loading && !error && deals.length === 0 && (
        <div className="text-xs text-slate-500 italic p-3 bg-slate-50 border border-slate-200 rounded">
          Esta empresa ainda não tem deals em Empreendimentos.
          {canWrite && (
            <>
              {' '}
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                className="text-violet-700 underline hover:no-underline"
              >
                Criar primeiro deal
              </button>
              .
            </>
          )}
        </div>
      )}

      {!loading && !error && deals.length > 0 && (
        <div className="space-y-1">
          {deals.map((d) => (
            <Link
              key={d.id}
              to={`/launches/deals/${d.id}`}
              className="block bg-white border border-slate-200 rounded px-2 py-1.5 text-xs hover:bg-violet-50"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="font-medium text-slate-800 truncate flex-1">{d.title}</div>
                <span className={`px-1.5 py-0.5 rounded text-[10px] ${STAGE_COLORS[d.stage] || 'bg-slate-100 text-slate-700'}`}>
                  {d.stage}
                </span>
              </div>
              <div className="flex items-center justify-between text-[11px] text-slate-500 mt-0.5">
                <span>{fmtBRL(d.valor_estimado)}</span>
                {d.next_step && <span className="truncate ml-2 text-right">{d.next_step}</span>}
              </div>
            </Link>
          ))}
        </div>
      )}

      <LaunchDealCreateModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => refetch()}
        defaultEmpresaId={empresaId}
      />
    </div>
  );
}

/**
 * Wrapper exportado com Error Boundary (Improvement Gemini).
 * Se EmpresaLaunchesTab quebrar, tab "Áreas" do EmpresaQuickViewModal continua funcionando.
 */
export default function EmpresaLaunchesTab({ empresaId }) {
  return (
    <ErrorBoundary FallbackComponent={TabFallback}>
      <EmpresaLaunchesTabBody empresaId={empresaId} />
    </ErrorBoundary>
  );
}
